// Live spoken-word total for the Script Editor.
//
// Maintains a rowId -> { text, count } cache plus a running total. On each pass
// only cells whose spoken text actually changed are re-counted; every unchanged
// row reuses its cached count. New rows (paste, insert, concurrent-merge) get
// counted, removed rows fall out of the cache — so load / paste / insert /
// delete / move / merge all resolve correctly without threading a manual delta
// through each mutation handler.
//
// The recount is debounced ~150ms so held keys and fast typists don't thrash;
// the displayed number may lag a keystroke, which is fine for an ambient
// read-aloud estimate. Recounting is O(rows) in cheap cache lookups; the
// expensive strip+split runs only on the cell(s) that changed.

import { useEffect, useRef, useState } from 'react';
import { countBodyWords, countCellWords } from '../utils/wordCount';

const DEBOUNCE_MS = 150;

export function useSpokenWordCount(draftBody, spokenKey) {
  const [total, setTotal] = useState(0);
  const cacheRef = useRef(new Map()); // rowId -> { text, count }
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    // No format / no spoken column -> nothing to count.
    if (!spokenKey || !Array.isArray(draftBody?.scenes)) {
      cacheRef.current = new Map();
      setTotal(0);
      return;
    }

    timerRef.current = setTimeout(() => {
      // countBodyWords is the shared rule (the API counts approvals with its twin); this hook only adds the per-row
      // cache. spokenKey is SPOKEN_COL[draftBody.format], the same column countBodyWords picks.
      const prev = cacheRef.current;
      const next = new Map();
      const sum = countBodyWords(draftBody, (text, row) => {
        const id = row?.id;
        const cached = id != null ? prev.get(id) : null;
        const count = (cached && cached.text === text) ? cached.count : countCellWords(text);
        if (id != null) next.set(id, { text, count });
        return count;
      });
      cacheRef.current = next;
      setTotal(sum);
    }, DEBOUNCE_MS);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [draftBody, spokenKey]);

  return total;
}
